import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import { defineMessage } from "react-intl";
import { IChangeEvent } from "react-jsonschema-form";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { useProperty } from "../../components/property-provider";
import originalSchema from "../../gen/schema/witbooking.backoffice.occupation.v1/Occupation.json";
import actions from "../../lib/actions";
import { getLanguageFromFullLocale } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { useInvalidateInventoryCaches } from "../inventory/inventory.hook";
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns } from "../shared/crud/list";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import * as TableRenderers from "../shared/renderers/table";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { translationFieldConfig } from "../shared/widgets/translation";
import { OccupationEntity } from "./domain/occupation.entity";
import { occupationsQueryKeys } from "./infrastructure/cache/OccupationsCacheStrategy";
import {
  createOccupation,
  deleteOccupation,
  getAllOccupations,
  getOccupation,
  updateOccupation,
} from "./infrastructure/occupation.api";
import {
  generateOccupationTicker,
  generateOccupationTranslation,
  occupationMessages,
  tableHeaderMessages,
} from "./messages";

const schema = {
  ...originalSchema,
  properties: {
    ...originalSchema.properties,
    seniors: {
      ...originalSchema.properties.seniors,
      minimum: 0,
    },
    adults: {
      ...originalSchema.properties.adults,
      minimum: 1,
    },
    juniors: {
      ...originalSchema.properties.juniors,
      minimum: 0,
    },
    children: {
      ...originalSchema.properties.children,
      minimum: 0,
    },
    babies: {
      ...originalSchema.properties.babies,
      minimum: 0,
    },
    autoGenerateTranslations: {
      type: "boolean",
    },
  },
};

const messages = {
  ...defaultCrudMessages,
  ...occupationMessages,
  patternValidationFailed: {
    ".ticker": defineMessage({
      id: "occupation.tickerPatternFailed",
      defaultMessage: "Must be uppercase. Doesn't allow blank spaces or special characters",
    }),
  },
  tableHeaders: tableHeaderMessages,
};

interface OccupationFilters {
  ticker: string;
}

function isAccommodationMode(formData: any): boolean {
  return formData?.ticker === "pax";
}

function buildFormConfiguration(allowedLocales: string[]): FormConfiguration<OccupationEntity> {
  return {
    __fieldOrder: [
      "ticker",
      "seniors",
      "adults",
      "juniors",
      "children",
      "babies",
      "autoGenerateTranslations",
      "translationsList",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    ticker: {
      readonly: true,
      messages: {
        label: tableHeaderMessages.ticker,
        helpText: messages.occupationTickerHelpText,
      },
    },
    seniors: {
      default: () => 0,
      readonly: isNew => !isNew,
      hidden: (_, formData) => isAccommodationMode(formData),
    },
    adults: {
      default: () => 2,
      readonly: isNew => !isNew,
      hidden: (_, formData) => isAccommodationMode(formData),
      transform: (originalValue, formData) => {
        return isAccommodationMode(formData) ? 1 : originalValue;
      },
    },
    juniors: {
      default: () => 0,
      readonly: isNew => !isNew,
      hidden: (_, formData) => isAccommodationMode(formData),
    },
    children: {
      default: () => 0,
      readonly: isNew => !isNew,
      hidden: (_, formData) => isAccommodationMode(formData),
    },
    babies: {
      default: () => 0,
      readonly: isNew => !isNew,
      hidden: (_, formData) => isAccommodationMode(formData),
    },
    inUse: {
      hidden: true,
    },
    pax: {
      hidden: true,
    },
    autoGenerateTranslations: {
      default: () => true,
      readonly: isNew => !isNew,
      widget: SwitchGroup,
      messages: {
        label: defineMessage({
          id: "occupation.autoGenerateTranslations.label",
          defaultMessage: "Auto-generate translations fields?",
        }),
      },
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["name", "description", "*"],
        name: {
          messages: {
            label: messages.occupationTranslationNameLabel,
          },
        },
        description: { hidden: true },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: messages.occupationTranslationsTitle,
      },
    },
  };
}

function buildTableColumns(): TableColumns<OccupationEntity> {
  return {
    ticker: {
      renderer: TableRenderers.StringRenderer,
      width: 140,
    },
    translationsList: {
      renderer: TableRenderers.mapRenderer(TableRenderers.StringRenderer, (v, intl) => {
        if (!v || v.length === 0) return undefined;
        const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
        return current?.name || v[0].name;
      }),
    },
    pax: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    seniors: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    adults: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    juniors: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    children: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
    babies: {
      renderer: TableRenderers.NumberRenderer,
      width: 100,
    },
  };
}

function OccupationCrud() {
  const allowedLocales = usePropertyLocales();
  const property = useProperty();
  const queryClient = useQueryClient();
  const invalidateInventoryCaches = useInvalidateInventoryCaches();

  const invalidateOccupations = useCallback(() => {
    queryClient.removeQueries({ queryKey: occupationsQueryKeys.list(property.ticker) });
    queryClient.removeQueries({ queryKey: occupationsQueryKeys.configurations(property.ticker) });
    return invalidateInventoryCaches(); // inventory entries are derived from occupations
  }, [queryClient, property.ticker, invalidateInventoryCaches]);

  const disableDelete = useCallback(
    (value: OccupationEntity) => value.inUse && "occupation.inUse",
    []
  );

  const handleOnChange = useCallback(
    (e: IChangeEvent<OccupationEntity>) => {
      const isEditing = window.location.pathname.includes("/edit");
      if (isEditing) {
        return e.formData;
      }
      const newData = { ...e.formData };
      newData.ticker = generateOccupationTicker(e.formData);
      if (newData.autoGenerateTranslations) {
        newData.translationsList = generateOccupationTranslation(e.formData, allowedLocales);
      }
      return newData;
    },
    [allowedLocales]
  );

  const handleGetAll = useCallback(
    () =>
      queryClient.ensureQueryData({
        queryKey: occupationsQueryKeys.list(property.ticker),
        queryFn: () => getAllOccupations(property.ticker, property.token),
        staleTime: 5 * 60 * 1000,
      }),
    [queryClient, property.ticker, property.token]
  );

  const handleCreate = useCallback(
    async (entity: OccupationEntity) => {
      const id = await createOccupation(entity);
      await invalidateOccupations();
      return id;
    },
    [invalidateOccupations]
  );

  const handleUpdate = useCallback(
    async (entity: OccupationEntity) => {
      await updateOccupation(entity);
      await invalidateOccupations();
    },
    [invalidateOccupations]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteOccupation(id);
      await invalidateOccupations();
    },
    [invalidateOccupations]
  );

  const tableColumns = useMemo(() => buildTableColumns(), []);
  const formConfiguration = useMemo(() => buildFormConfiguration(allowedLocales), [allowedLocales]);
  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <SimpleCrud<OccupationEntity, OccupationFilters>
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      messages={messages}
      onGetAll={handleGetAll}
      onGetOne={getOccupation}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      canCreate={actions.CreateOccupation}
      canUpdate={actions.ModifyOccupation}
      disableDelete={disableDelete}
      section={SECTION.OCCUPATIONS}
      onChange={handleOnChange}
      listHeight={"100%"}
    />
  );
}

export default withAuthorizationVerifier(OccupationCrud);
